import { useState } from 'react'
import type { CSSProperties, KeyboardEvent, MouseEvent, ReactNode } from 'react'

interface CardProps {
  children: ReactNode
  /** Padding override. Number → px, string → raw CSS. Defaults to 16. */
  padding?: number | string
  /**
   * When true, the card lights up on hover (border tints toward primary,
   * lifts 1 px) and presses on `:active`. Use for cards that the whole
   * surface acts as a single click target.
   */
  interactive?: boolean
  style?: CSSProperties
  className?: string
  'data-testid'?: string
  /**
   * Optional click handler. When provided alongside ``interactive`` the
   * whole card surface (including its 16-px padding) is the target so the
   * user can click anywhere on the card body — not just an inner div.
   */
  onClick?: (event: MouseEvent<HTMLDivElement>) => void
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void
  role?: string
  tabIndex?: number
  'aria-label'?: string
  /**
   * Arbitrary `data-*` passthroughs land on the underlying div. Lets
   * callers attach domain-specific attributes (`data-leave-id`,
   * `data-status`, …) without wrapping the Card in another element.
   */
  [dataAttr: `data-${string}`]: string | number | boolean | undefined
}

/**
 * Plain rectangular surface used to group related content. Pure wrapper —
 * no internal state, no behaviour. Background, border, and radius come from
 * design tokens so the warm-paper theme stays consistent across the app.
 *
 * Pass `interactive` for the card to react to hover and press; the card
 * itself is still not a button — wrap it in a `<button>` or attach an
 * `onClick` from the caller.
 */
export function Card({
  children,
  padding = 16,
  interactive = false,
  style,
  className,
  'data-testid': dataTestId,
  onClick,
  onKeyDown,
  role,
  tabIndex,
  'aria-label': ariaLabel,
  ...rest
}: CardProps): JSX.Element {
  // Filter `rest` down to data-* attributes only (the indexed signature
  // permits other strings via TS quirks; runtime keeps things tight).
  const dataAttrs: Record<string, string | number | boolean | undefined> = {}
  for (const [key, value] of Object.entries(rest)) {
    if (key.startsWith('data-')) dataAttrs[key] = value
  }
  const [hover, setHover] = useState<boolean>(false)
  const [pressed, setPressed] = useState<boolean>(false)

  const transitionDuration =
    interactive && pressed
      ? 'var(--motion-duration-fast)'
      : 'var(--motion-duration-base)'

  const baseStyle: CSSProperties = {
    background: 'var(--surface)',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'var(--border)',
    borderRadius: 12,
    padding,
    transition:
      `background ${transitionDuration} var(--motion-ease-out),` +
      ` border-color ${transitionDuration} var(--motion-ease-out),` +
      ` transform ${transitionDuration} var(--motion-ease-out),` +
      ` box-shadow ${transitionDuration} var(--motion-ease-out)`,
  }

  if (interactive) {
    if (hover) {
      baseStyle.borderColor = 'var(--primary-line)'
      baseStyle.transform = 'translateY(-1px)'
      baseStyle.boxShadow = 'var(--shadow-2)'
    }
    if (pressed) {
      baseStyle.transform = 'scale(0.98)'
    }
  }

  const interactionHandlers = interactive
    ? {
        onMouseEnter: () => setHover(true),
        onMouseLeave: () => {
          setHover(false)
          setPressed(false)
        },
        onMouseDown: () => setPressed(true),
        onMouseUp: () => setPressed(false),
      }
    : {}

  return (
    <div
      data-testid={dataTestId}
      data-interactive={interactive ? 'true' : undefined}
      className={className}
      style={{
        ...baseStyle,
        ...style,
        cursor: onClick ? 'pointer' : style?.cursor,
      }}
      role={role}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={onKeyDown}
      {...dataAttrs}
      {...interactionHandlers}
    >
      {children}
    </div>
  )
}
